import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

export type ThemeMode = 'light' | 'dark';

export interface ThemeContextValue {
  mode: ThemeMode;
  toggle: () => void;
  setMode: (mode: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

const STORAGE_KEY = 'aliza-theme';

export interface ThemeProviderProps {
  children: ReactNode;
  defaultMode?: ThemeMode;
  storage?: { getItem(key: string): string | null; setItem(key: string, value: string): void };
}

export function PortalThemeProvider({ children, defaultMode = 'dark', storage }: ThemeProviderProps) {
  const store = storage ?? (typeof localStorage !== 'undefined' ? localStorage : null);

  const [mode, setModeState] = useState<ThemeMode>(() => {
    const stored = store?.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
    return defaultMode;
  });

  useEffect(() => {
    store?.setItem(STORAGE_KEY, mode);
  }, [mode, store]);

  const toggle = useCallback(() => {
    setModeState((prev) => (prev === 'light' ? 'dark' : 'light'));
  }, []);

  const setMode = useCallback((newMode: ThemeMode) => {
    setModeState(newMode);
  }, []);

  const value = useMemo<ThemeContextValue>(() => ({ mode, toggle, setMode }), [mode, toggle, setMode]);

  return (
    <ThemeContext.Provider value={value}>
      <div data-theme={mode}>{children}</div>
    </ThemeContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function usePortalTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('usePortalTheme must be used within a PortalThemeProvider');
  return ctx;
}
